import { Input } from '@base-ui/react/input';
import styled, { css } from 'styled-components';

export const FormStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

export const FormRow = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
`;

// Side by side when there's room.
export const FieldPair = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;

  & > * {
    min-width: 8rem;
  }
`;

export const FormLabel = styled.label`
  color: var(--grey-800);
  font-size: 0.8125rem;
  font-weight: 600;
`;

export const FieldHint = styled.p`
  color: var(--grey-600);
  font-size: 0.8125rem;
  line-height: 1.4;
  margin: 0;
`;

export const fieldCss = css`
  background-color: var(--bg-default);
  border: 1px solid var(--grey-450);
  border-radius: 0.375rem;
  box-sizing: border-box;
  color: var(--grey-900);
  font-family: inherit;
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
  width: 100%;

  &:focus {
    outline: 2px solid var(--blue);
    outline-offset: -1px;
  }

  &:disabled {
    background-color: var(--grey-100);
    border-color: var(--grey-300);
    color: var(--grey-600);
    cursor: not-allowed;
  }
`;

export const TextInput = styled(Input)`
  ${fieldCss}
`;

// The native arrow sits almost against the edge; draw our own, inset to match
// the text's padding. Its color follows the theme via --select-chevron.
export const selectCss = css`
  appearance: none;
  background-image: var(--select-chevron);
  background-position: right 0.75rem center;
  background-repeat: no-repeat;
  background-size: 0.75rem 0.5rem;
  cursor: pointer;
  padding-right: 2.25rem;

  /* High-contrast modes drop background images; use the native arrow there. */
  @media (forced-colors: active) {
    appearance: auto;
    background-image: none;
    padding-right: 0.75rem;
  }
`;

export const Select = styled.select`
  ${fieldCss}
  ${selectCss}
`;

export const CheckboxLabel = styled.label<{ $disabled?: boolean }>`
  align-items: center;
  color: ${(p) => (p.$disabled ? 'var(--grey-550)' : 'var(--grey-800)')};
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  font-size: 0.875rem;
  gap: 0.5rem;

  input:disabled {
    cursor: not-allowed;
  }
`;

const noticeCss = css`
  border-radius: 0.25rem;
  color: var(--grey-800);
  font-size: 0.8125rem;
  line-height: 1.4;
  margin: 0;
  padding: 0.5rem 0.75rem;
`;

export const InfoNotice = styled.p`
  ${noticeCss}
  background: color-mix(in srgb, var(--blue) 10%, transparent);
  border-left: 3px solid var(--blue);
`;

export const SuccessNotice = styled.p`
  ${noticeCss}
  background: color-mix(in srgb, var(--success) 12%, transparent);
  border-left: 3px solid var(--success);
`;

// "Filtered to …" above a list, with a link to clear it.
export const FilterBanner = styled.div`
  align-items: center;
  background: color-mix(in srgb, var(--blue) 8%, transparent);
  border-left: 3px solid var(--blue);
  border-radius: 4px;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-bottom: var(--component-margin);
  padding: 0.625rem 0.875rem;

  a {
    color: var(--blue);
  }
`;

export const WarningNotice = styled.p`
  ${noticeCss}
  background: color-mix(in srgb, var(--orange) 12%, transparent);
  border-left: 3px solid var(--orange);
`;

export const ErrorNotice = styled.div`
  ${noticeCss}
  background: color-mix(in srgb, var(--error) 8%, transparent);
  border-left: 3px solid var(--error);
`;

export const ErrorText = styled.div`
  color: var(--error);
  font-size: 0.8125rem;
`;
